import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  Grid,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Backdrop, CircularProgress } from "@mui/material";
import EnhancedPageTitle from "../../Component/EnhancedPageTitle";
import {
  createProject,
  GetCustomers,
  GetPayTerms,
} from "../../services/projectService";

interface FormDataType {
  projectName: string;
  customerId: string | number;
  
  fixed: string;
  payTermsId: string | number;
  isActive: boolean;
  startDate: string;
  endDate: string;
}

const AddProject = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState<FormDataType>({
    projectName: "",
    customerId: "",
   
    fixed: "",
    payTermsId: "",
    isActive: true,
    startDate: "",
    endDate: "",
  });

  const [customers, setCustomers] = useState<any[]>([]);
  const [salesPersons, setSalesPersons] = useState<any[]>([]);
  const [payTerms, setPayTerms] = useState<any[]>([]);

  const [selectedCustomerId, setSelectedCustomerId] = useState<number | "">("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });
  const [shouldNavigate, setShouldNavigate] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadMasterData = async () => {
      try {
        const [customerRes, payTermsRes] = await Promise.all([
          GetCustomers(),
          GetPayTerms(),
        ]);
        setCustomers(customerRes);
        setPayTerms(payTermsRes);
      } catch (err) {
        console.error("Error loading master data", err);
      }
    };

    loadMasterData();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: ["customerId", "salesPersonId", "payTermsId"].includes(name)
        ? (value === "" ? "" : Number(value))
        : value,
    }));
  };

  const handleCustomerSelect = (value: number | "") => {
    setSelectedCustomerId(value);
    setFormData((prev) => ({
      ...prev,
      customerId: value === "" ? "" : Number(value),
    }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const handleSaveClick = () => {
    setConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    setConfirmOpen(false);
  };

  const handleReset = () => {
    setFormData({
      projectName: "",
      customerId: "",
     
      fixed: "",
      payTermsId: "",
      isActive: true,
      startDate: "",
      endDate: "",
    });
    setSelectedCustomerId("");
  };

  const handleSubmit = async () => {
    setConfirmOpen(false);
    setLoading(true);

    try {
      const payload = {
        projectName: formData.projectName,
        customerId: formData.customerId,
        
        fixed: formData.fixed === "true",
        payTermsId: formData.payTermsId,
        startDate: formData.startDate,
        endDate: formData.endDate,
        createdOn: new Date().toISOString(),
        createdBy: 0,
        modifiedOn: new Date().toISOString(),
        modifiedBy: 0,
        isDeleted: false,
        isActive: formData.isActive,
        projectId: 0,
      };

      await createProject(payload);

      setSnackbar({
        open: true,
        message: "Project added successfully",
        severity: "success",
      });

      setShouldNavigate(true);
    } catch (error) {
      console.error("Error adding project:", error);
      setSnackbar({
        open: true,
        message: "Error adding project",
        severity: "error",
      });
      setShouldNavigate(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ bgcolor: "#F0F2F5", p: 3, minHeight: "100vh" }}>
      <EnhancedPageTitle
        title="Add Project"
        button1Text="Back"
        button1Action={() => navigate("/Projects")}
      />

      <Card
        elevation={1}
        sx={{ bgcolor: "#fff", p: 4, maxWidth: "700px", mx: "auto" }}
      >
        <Typography variant="h6" mb={3}>
          New Project
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <input
              type="text"
              name="projectName"
              value={formData.projectName}
              onChange={handleChange}
              placeholder="Project Name"
              style={{ width: "100%", padding: "10px" }}
            />
          </Grid>

          <Grid item xs={12}>
            <select
              value={selectedCustomerId}
              onChange={(e) => handleCustomerSelect(Number(e.target.value))}
              style={{ width: "100%", padding: "10px" }}
            >
              <option value="">--Select Customer--</option>
              {customers.map((cust) => (
                <option key={cust.customerId} value={cust.customerId}>
                  {cust.customerName}
                </option>
              ))}
            </select>
          </Grid>

         

          <Grid item xs={12}>
            <select
              name="fixed"
              value={formData.fixed}
              onChange={handleChange}
              style={{ width: "100%", padding: "10px" }}
            >
              <option value="">--Select--</option>
              <option value="true">Fixed</option>
              <option value="false">TM</option>
            </select>
          </Grid>

          <Grid item xs={12}>
            <select
              name="payTermsId"
              value={formData.payTermsId}
              onChange={handleChange}
              style={{ width: "100%", padding: "10px" }}
            >
              <option value="">--Select Payment Terms--</option>
              {payTerms.map((pt) => (
                <option key={pt.tableID} value={pt.tableID}>
                  {pt.tableName}
                </option>
              ))}
            </select>
          </Grid>

          <Grid item xs={12}>
            <input
              type="date"
              name="startDate"
              value={formData.startDate}
              onChange={handleChange}
              style={{ width: "100%", padding: "10px" }}
            />
          </Grid>

          <Grid item xs={12}>
            <input
              type="date"
              name="endDate"
              value={formData.endDate}
              onChange={handleChange}
              style={{ width: "100%", padding: "10px" }}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isActive}
                  onChange={handleCheckboxChange}
                  name="isActive"
                />
              }
              label="Is Active"
            />
          </Grid>

          <Grid item xs={12} display="flex" justifyContent="flex-end" gap={2}>
            <Button variant="contained" color="primary" onClick={handleSaveClick}>
              Save
            </Button>
            <Button variant="outlined" color="secondary" onClick={handleReset}>
              Reset
            </Button>
          </Grid>
        </Grid>
      </Card>

      <Dialog open={confirmOpen} onClose={handleConfirmClose}>
        <DialogTitle>Confirm Save</DialogTitle>
        <DialogContent>
          Are you sure you want to save this project?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmClose} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSubmit} color="primary" variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={(event, reason) => {
          if (reason === "clickaway") return;
          setSnackbar((prev) => ({ ...prev, open: false }));
          if (shouldNavigate && snackbar.severity === "success") {
            navigate("/Projects");
          }
        }}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default AddProject;
